import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, Eye, ExternalLink, FileText, Upload } from "lucide-react";
import { apiRequest } from "../../Api/Api";
import { ENDPOINTS } from "../../Api/Endpoints";
import PatientAppLayout from "../../components/PatientAppLayout/PatientAppLayout";
import { SkeletonBlock } from "../../components/Skeleton/Skeleton";
import { useInfiniteScrollLoad } from "../../hooks/useInfiniteScrollLoad";
import "./PatientDocuments.scss";

const MAX_FILE_BYTES = 20 * 1024 * 1024;
const PAGE_SIZE = 20;

const typeLabel = (t) => {
  const u = String(t || "").toLowerCase();
  if (u === "report") return "Report";
  if (u === "insurance") return "Insurance";
  return "Prescription";
};

const formatSizeKb = (bytes) => {
  const n = Number(bytes) || 0;
  if (n <= 0) return "—";
  const kb = Math.max(1, Math.round(n / 1024));
  return `${kb} KB`;
};

const statusLabel = (row) => {
  const s = String(row?.status || "pending").toLowerCase();
  if (s === "accepted") return "Verified";
  if (s === "rejected") return "Rejected";
  return "Pending review";
};

/** @returns {"image" | "pdf" | null} */
const inlinePreviewKind = (row) => {
  const name = String(row?.originalName || "").toLowerCase();
  const ft = String(row?.fileType || "").toLowerCase();
  if (ft.startsWith("image/") || /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(name)) return "image";
  if (ft === "application/pdf" || /\.pdf$/i.test(name)) return "pdf";
  return null;
};

function PatientDocImagePreview({ url, name, onOpen }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <button
      type="button"
      className="pdPreviewInline pdPreviewInline--image"
      onClick={onOpen}
      aria-label={`Open preview of ${name}`}
    >
      <img
        src={url}
        alt=""
        className="pdPreviewInline__img"
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </button>
  );
}

function PatientDocumentsSkeleton() {
  const rowSets = [
    [
      { previewH: 72, nameW: "min(100%, 220px)", pill1: 76, pill2: 104 },
      { previewH: 0, nameW: "min(100%, 180px)", pill1: 68, pill2: 96 },
      { previewH: 56, nameW: "min(100%, 260px)", pill1: 72, pill2: 88 },
    ],
    [
      { previewH: 0, nameW: "min(100%, 200px)", pill1: 80, pill2: 100 },
      { previewH: 68, nameW: "min(100%, 190px)", pill1: 70, pill2: 104 },
    ],
  ];
  return (
    <div className="pdSkRoot" aria-busy="true" aria-label="Loading medical files">
      <span className="pdSkSrOnly">Loading medical files</span>
      <div className="pdRequestGroupList pdRequestGroupList--skeleton">
        {rowSets.map((rows, gi) => (
          <section key={`pd-sk-g-${gi}`} className="pdRequestBlock pdRequestBlock--skeleton">
            <header className="pdRequestBlock__head">
              <div className="pdRequestBlock__hero">
                <SkeletonBlock height={11} width={gi === 0 ? 104 : 72} className="pdSkKicker" />
                <SkeletonBlock
                  height={20}
                  width={gi === 0 ? "min(100%, 72%)" : "min(100%, 58%)"}
                  className="pdSkBlockTitle"
                />
              </div>
              <SkeletonBlock height={36} width={124} className="pdSkCtaGhost" />
            </header>
            <div className="pdTableWrap">
              <table className="pdTable pdTable--skeleton">
                <thead aria-hidden>
                  <tr>
                    {[72, 52, 58, 40, 62].map((w, i) => (
                      <th key={`pd-sk-th-${i}`}>
                        <SkeletonBlock height={10} width={`${w}%`} className="pdSkThBar" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, ri) => (
                    <tr key={`pd-sk-r-${gi}-${ri}`} className="pdRow pdSkRow">
                      <td data-label="File">
                        <div className="pdFileBlock">
                          {r.previewH > 0 ? (
                            <SkeletonBlock
                              height={r.previewH}
                              width="min(100%, 240px)"
                              className="pdSkPreviewThumb"
                            />
                          ) : null}
                          <div className="pdFileCell">
                            <SkeletonBlock width={22} height={22} className="pdSkFileIcon" />
                            <SkeletonBlock height={15} width={r.nameW} className="pdSkFileName" />
                          </div>
                        </div>
                      </td>
                      <td data-label="Type">
                        <SkeletonBlock height={26} width={r.pill1} className="pdSkTypeGhost" />
                      </td>
                      <td data-label="Status">
                        <SkeletonBlock height={26} width={r.pill2} className="pdSkStatusGhost" />
                      </td>
                      <td data-label="Size">
                        <SkeletonBlock height={14} width={48} className="pdSkSize" />
                      </td>
                      <td data-label="Actions">
                        <div className="pdTableActions">
                          <SkeletonBlock height={32} width={76} className="pdSkActBtn" />
                          <SkeletonBlock height={32} width={84} className="pdSkActBtn" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function PatientDocPdfPreview({ url, name, onOpen }) {
  return (
    <button
      type="button"
      className="pdPreviewInline pdPreviewInline--pdf"
      onClick={onOpen}
      aria-label={`Open PDF preview of ${name}`}
    >
      <div className="pdPreviewInline__pdfShell">
        <iframe
          src={url}
          className="pdPreviewInline__iframe"
          title={`PDF preview: ${name}`}
          loading="lazy"
        />
      </div>
      <span className="pdPreviewInline__pdfHint">PDF · tap to expand</span>
    </button>
  );
}

export default function PatientDocuments() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const reuploadParam = searchParams.get("reupload") || "";
  const rowRefs = useRef({});
  const fileInputRefs = useRef({});

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [verificationStatus, setVerificationStatus] = useState("");
  const [rejectedDocumentCount, setRejectedDocumentCount] = useState(0);
  const [reuploadingId, setReuploadingId] = useState("");
  const [reuploadError, setReuploadError] = useState("");
  const [preview, setPreview] = useState(null);
  const nextPageRef = useRef(1);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);

  const load = useCallback(async ({ reset = true } = {}) => {
    if (!reset) {
      if (loadingMoreRef.current || !hasMoreRef.current) return;
      loadingMoreRef.current = true;
      setLoadingMore(true);
    } else {
      nextPageRef.current = 1;
      hasMoreRef.current = true;
      setHasMore(true);
      setLoading(true);
      setError("");
    }

    const targetPage = nextPageRef.current;

    try {
      const res = await apiRequest({
        method: "GET",
        url: ENDPOINTS.GET_MY_PRESCRIPTIONS,
        params: { page: targetPage, limit: PAGE_SIZE },
      });
      const list = Array.isArray(res?.items) ? res.items : [];
      const total = Math.max(0, Number(res?.pagination?.total) || 0);

      setItems((prev) => (reset ? list : [...prev, ...list]));
      if (reset) {
        setVerificationStatus(String(res?.verificationStatus || ""));
        setRejectedDocumentCount(Math.max(0, Number(res?.rejectedDocumentCount) || 0));
      }

      const loadedThrough = targetPage * PAGE_SIZE;
      const more = total > 0 ? loadedThrough < total : list.length >= PAGE_SIZE;
      hasMoreRef.current = more;
      setHasMore(more);
      nextPageRef.current = targetPage + 1;
    } catch (err) {
      if (reset) {
        setError(err?.message || "Could not load files.");
        setItems([]);
        setHasMore(false);
        hasMoreRef.current = false;
      }
    } finally {
      if (reset) setLoading(false);
      else {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, []);

  const loadMore = useCallback(() => {
    void load({ reset: false });
  }, [load]);

  const sentinelRef = useInfiniteScrollLoad({
    hasMore,
    loading,
    loadingMore,
    onLoadMore: loadMore,
  });

  useEffect(() => {
    void load({ reset: true });
  }, [load]);

  useEffect(() => {
    const onRefresh = (e) => {
      const t = String(e?.detail?.type || "");
      if (t === "prescription_rejected" || t === "prescription_bulk_rejected" || t === "me_refresh") {
        void load({ reset: true });
      }
    };
    window.addEventListener("healbid:patient-notification", onRefresh);
    return () => window.removeEventListener("healbid:patient-notification", onRefresh);
  }, [load]);

  useEffect(() => {
    if (!reuploadParam || loading) return;
    const el = rowRefs.current[reuploadParam];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [reuploadParam, loading, items.length]);

  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => {
      if (e.key === "Escape") setPreview(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview]);

  const triggerReuploadPicker = (docId) => {
    const input = fileInputRefs.current[String(docId)];
    if (input) input.click();
  };

  const handleReuploadFileSelected = async (docId, file) => {
    if (!docId || !file) return;
    if (file.size > MAX_FILE_BYTES) {
      setReuploadError("File too large. Please upload a file up to 20 MB.");
      return;
    }
    setReuploadError("");
    setReuploadingId(String(docId));
    try {
      const metaRes = await apiRequest({
        method: "POST",
        url: `/app/prescriptions/${docId}/upload-url`,
        data: { filename: file.name },
      });
      const meta = metaRes?.data || metaRes || {};
      if (!meta.uploadUrl || !meta.key) throw new Error("Upload URL not received");

      const putRes = await fetch(meta.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });
      if (!putRes.ok) throw new Error("Failed to upload file");

      await apiRequest({
        method: "PUT",
        url: `/app/prescriptions/${docId}`,
        data: { key: meta.key, originalName: file.name },
      });

      setReuploadError("");
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("reupload");
          return next;
        },
        { replace: true }
      );
      await load({ reset: true });
    } catch (err) {
      setReuploadError(err?.message || "Re-upload failed");
    } finally {
      setReuploadingId("");
    }
  };

  const linkedLabel = useCallback((leaderRow, groupRows) => {
    const direct = String(leaderRow?.linkedRequestLabel || "").trim();
    if (direct) return direct.replace(/\s*·\s*/g, " > ");
    if (leaderRow?.matchRequestId) {
      const rows =
        Array.isArray(groupRows) && groupRows.length ? groupRows : leaderRow ? [leaderRow] : [];
      const ref =
        rows.length > 0
          ? rows.reduce((best, r) => {
              const t = new Date(r?.createdAt || 0).getTime();
              const bt = new Date(best?.createdAt || 0).getTime();
              return Number.isFinite(t) && t >= bt ? r : best;
            })
          : leaderRow;
      const d = ref?.createdAt ? new Date(ref.createdAt) : null;
      if (d && Number.isFinite(d.getTime())) {
        return d.toLocaleDateString(undefined, { dateStyle: "medium" });
      }
      return "Linked documents";
    }
    return "—";
  }, []);

  const requestGroups = useMemo(() => {
    const map = new Map();
    for (const row of items) {
      const mid = row.matchRequestId ? String(row.matchRequestId).trim() : "";
      const key = mid || "__account__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    }
    return [...map.entries()]
      .map(([key, rows]) => {
        const requestId = key === "__account__" ? "" : key;
        const title =
          key === "__account__"
            ? "Account & other files"
            : linkedLabel(rows[0], rows);
        return { key, requestId, title, rows };
      })
      .sort((a, b) => {
        const maxTs = (rs) =>
          Math.max(
            0,
            ...rs.map((r) => {
              const t = new Date(r.createdAt || 0).getTime();
              return Number.isFinite(t) ? t : 0;
            })
          );
        return maxTs(b.rows) - maxTs(a.rows);
      });
  }, [items, linkedLabel]);

  const highlightId = reuploadParam.trim();

  const verificationChip = useMemo(() => {
    const raw = String(verificationStatus || "").trim();
    if (!raw) return null;
    const slug = raw.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "unknown";
    const label = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    return { slug, label };
  }, [verificationStatus]);

  const rejectedRows = useMemo(
    () => items.filter((row) => String(row?.status || "").toLowerCase() === "rejected"),
    [items]
  );
  const rejectedCount = rejectedDocumentCount > 0 ? rejectedDocumentCount : rejectedRows.length;
  const firstRejectedId = useMemo(() => {
    const r = rejectedRows[0];
    const id = r?._id ?? r?.id;
    return id != null ? String(id).trim() : "";
  }, [rejectedRows]);

  const scrollToRejected = useCallback(() => {
    if (!firstRejectedId) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("reupload", firstRejectedId);
        return next;
      },
      { replace: true }
    );
  }, [firstRejectedId, setSearchParams]);

  const openRequest = (requestId) => {
    if (!requestId) return;
    navigate(`/patient-match-request/${encodeURIComponent(requestId)}`, {
      state: { requestRow: { matchRequestId: requestId } },
    });
  };

  return (
    <PatientAppLayout activeNav="documents">
      <div className="pdPage pdPage--patientShell">
        <div className="pdContainer">
          <section className="pdSurface">
            <header className="pdHead">
              <div className="pdHead__row">
                <h1>Your medical files</h1>
                {verificationChip ? (
                  <span
                    className={`pdVerifyChip pdVerifyChip--${verificationChip.slug}`}
                    title="Account verification"
                  >
                    Account · {verificationChip.label}
                  </span>
                ) : null}
              </div>
            </header>

            {!loading && rejectedCount > 0 ? (
              <div className="pdBanner pdBanner--alert" role="alert">
                {rejectedCount === 1
                  ? "One file was not approved — replace it below."
                  : `${rejectedCount} files were not approved — replace each below.`}{" "}
                {firstRejectedId ? (
                  <button type="button" className="pdBannerBtn" onClick={scrollToRejected}>
                    Go to file
                  </button>
                ) : null}
              </div>
            ) : null}

            {highlightId ? (
              <div className="pdBanner" role="status">
                Replace the highlighted file; it will show as <em>Pending review</em> after upload.
              </div>
            ) : null}

            {loading ? <PatientDocumentsSkeleton /> : null}
            {!loading && error ? <p className="pdState pdState--error">{error}</p> : null}
            {reuploadError ? <p className="pdState pdState--error">{reuploadError}</p> : null}

            {!loading && !error && items.length === 0 ? (
              <div className="pdEmpty">
                <p>No files yet.</p>
                <button type="button" className="pdBtn" onClick={() => navigate("/request/new")}>
                  Start a care request
                </button>
              </div>
            ) : null}

            {!loading && !error && items.length > 0 ? (
              <div className="pdRequestGroupList">
                {requestGroups.map((group) => (
                  <section
                    key={group.key}
                    className="pdRequestBlock"
                    aria-labelledby={`pd-req-head-${group.key}`}
                  >
                    <header className="pdRequestBlock__head">
                      <div className="pdRequestBlock__hero">
                        <span className="pdRequestBlock__kicker">
                          {group.requestId ? "Care request" : "Account"}
                        </span>
                        <h2 className="pdRequestBlock__title" id={`pd-req-head-${group.key}`}>
                          {group.title}
                        </h2>
                      </div>
                      {group.requestId ? (
                        <button
                          type="button"
                          className="pdRequestBlock__cta"
                          onClick={() => openRequest(group.requestId)}
                        >
                          Open request
                          <ChevronRight size={16} strokeWidth={2} aria-hidden />
                        </button>
                      ) : null}
                    </header>
                    <div className="pdTableWrap">
                      <table className="pdTable">
                        <thead>
                          <tr>
                            <th>File</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Size</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.rows.map((row) => {
                            const id = String(row._id || row.id || "");
                            const name = String(row.originalName || "File").trim() || "File";
                            const st = String(row.status || "pending").toLowerCase();
                            const isRejected = st === "rejected";
                            const isHighlight = highlightId && id === highlightId;
                            const busy = reuploadingId === id;
                            const fileUrl = String(row.fileUrl || "").trim();
                            const previewKind = fileUrl ? inlinePreviewKind(row) : null;
                            const openPreview = () => {
                              if (!fileUrl || !previewKind) return;
                              setPreview({ url: fileUrl, name, kind: previewKind });
                            };
                            return (
                              <tr
                                key={id || `${group.key}-${name}`}
                                ref={(el) => {
                                  if (id) rowRefs.current[id] = el;
                                }}
                                className={isHighlight ? "pdRow pdRow--highlight" : "pdRow"}
                              >
                                <td data-label="File">
                                  <div className="pdFileBlock">
                                    {fileUrl && previewKind === "image" ? (
                                      <PatientDocImagePreview url={fileUrl} name={name} onOpen={openPreview} />
                                    ) : null}
                                    {fileUrl && previewKind === "pdf" ? (
                                      <PatientDocPdfPreview url={fileUrl} name={name} onOpen={openPreview} />
                                    ) : null}
                                    <span className="pdFileCell">
                                      <span className="pdFileIcon" aria-hidden>
                                        <FileText size={18} strokeWidth={2} />
                                      </span>
                                      {fileUrl && previewKind ? (
                                        <button
                                          type="button"
                                          className="pdFileLink pdFileLink--btn"
                                          onClick={openPreview}
                                        >
                                          {name}
                                        </button>
                                      ) : fileUrl && !isRejected ? (
                                        <a href={fileUrl} className="pdFileLink" target="_blank" rel="noreferrer">
                                          {name}
                                        </a>
                                      ) : (
                                        <span className="pdFileName">{name}</span>
                                      )}
                                    </span>
                                    {isRejected && row.rejectionReason ? (
                                      <p className="pdRejectNote">Reason: {String(row.rejectionReason)}</p>
                                    ) : null}
                                  </div>
                                </td>
                                <td data-label="Type">
                                  <span className="pdTypePill">{typeLabel(row.type)}</span>
                                </td>
                                <td data-label="Status">
                                  <span className={`pdStatusPill pdStatusPill--${st || "pending"}`}>
                                    {statusLabel(row)}
                                  </span>
                                </td>
                                <td className="pdMuted" data-label="Size">
                                  {formatSizeKb(row.fileSize)}
                                </td>
                                <td data-label="Actions">
                                  <div className="pdTableActions">
                                    {fileUrl && previewKind ? (
                                      <button
                                        type="button"
                                        className="pdBtn pdBtn--small pdBtn--ghost"
                                        onClick={openPreview}
                                      >
                                        <Eye size={14} aria-hidden />
                                        View
                                      </button>
                                    ) : null}
                                    {fileUrl && !previewKind ? (
                                      <a
                                        href={fileUrl}
                                        className="pdBtn pdBtn--small pdBtn--ghost pdTableActions__link"
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        <ExternalLink size={14} aria-hidden />
                                        Open
                                      </a>
                                    ) : null}
                                    {isRejected ? (
                                      <>
                                        <input
                                          ref={(el) => {
                                            fileInputRefs.current[id] = el;
                                          }}
                                          type="file"
                                          accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,application/pdf,image/*"
                                          className="pdHiddenInput"
                                          aria-hidden
                                          tabIndex={-1}
                                          onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            e.target.value = "";
                                            if (f) void handleReuploadFileSelected(id, f);
                                          }}
                                        />
                                        <button
                                          type="button"
                                          className="pdBtn pdBtn--small"
                                          disabled={busy}
                                          onClick={() => triggerReuploadPicker(id)}
                                        >
                                          <Upload size={14} aria-hidden />
                                          {busy ? "Uploading…" : "Replace file"}
                                        </button>
                                      </>
                                    ) : null}
                                    {!fileUrl && !isRejected ? (
                                      <span className="pdMuted">—</span>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ))}
                {hasMore || loadingMore ? (
                  <div ref={sentinelRef} className="pdLoadMore" aria-live="polite">
                    {loadingMore ? "Loading more files…" : ""}
                  </div>
                ) : null}
              </div>
            ) : null}

            {preview ? (
              <div
                className="pdPreviewBackdrop"
                role="dialog"
                aria-modal="true"
                aria-label="File preview"
                onClick={() => setPreview(null)}
              >
                <div className="pdPreviewPanel" onClick={(e) => e.stopPropagation()}>
                  <header className="pdPreviewHead">
                    <p className="pdPreviewTitle">{preview.name}</p>
                    <button
                      type="button"
                      className="pdPreviewClose"
                      onClick={() => setPreview(null)}
                      aria-label="Close preview"
                    >
                      ×
                    </button>
                  </header>
                  <div className="pdPreviewBody">
                    {preview.kind === "image" ? (
                      <img src={preview.url} alt={preview.name} className="pdPreviewImg" />
                    ) : (
                      <div className="pdPreviewPdf">
                        <iframe title={preview.name} src={preview.url} className="pdPreviewIframe" />
                        <a
                          href={preview.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="pdPreviewNewTab"
                        >
                          Open in new tab
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </PatientAppLayout>
  );
}
